/**
 * Navigation bar component that provides the main navigation interface for the application.
 * Includes user authentication status, language selection, and responsive mobile menu.
 */

import { useMsal } from "@azure/msal-react";
import { Link } from "react-router-dom";
import LanguageSelector from "./LanguageSelector";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
//import Logo from "../assets/logo.png";

/**
 * Navbar component that displays the main navigation interface.
 * Features:
 * - Responsive design with mobile menu
 * - User authentication status and logout functionality
 * - Language selection
 * - Scroll-based styling changes
 * @returns {JSX.Element} The rendered Navbar component
 */
const Navbar = () => {
  const { instance, accounts } = useMsal();
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  /**
   * Effect hook to handle scroll-based styling changes
   * Updates the navbar appearance when the page is scrolled
   */
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 0;
      setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /**
   * Handles the user logout process using MSAL
   */
  const handleLogout = () => {
    instance.logoutPopup();
  };

  const isAdmin =
    accounts[0]?.idTokenClaims?.roles?.includes("Admin") ?? false;

  return (
    <nav
      className={`fixed top-0 w-full z-50 h-14 flex items-center bg-white/95 backdrop-blur-sm border-b border-gray-200/80 shadow-sm transition-shadow duration-300 ${
        scrolled ? "shadow-md" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Brand */}
          <Link to="/" className="flex-shrink-0 flex items-center">
            <span className="text-xl sm:text-2xl font-bold tracking-tight bg-gradient-to-r from-[#cabb78] to-[#b5a666] bg-clip-text text-transparent hover:opacity-90 transition-opacity">
              Kiinteistömittaus
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6 text-gray-600">
            <LanguageSelector />
            {accounts.length > 0 && (
              <>
                <span className="w-px h-5 bg-gray-200" aria-hidden />
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="text-sm font-medium text-gray-600 hover:text-[#b5a666] transition-colors"
                  >
                    {t("Admin")}
                  </Link>
                )}
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#cabb78] to-[#b5a666] flex items-center justify-center ring-2 ring-gray-100 flex-shrink-0">
                    <span className="text-white text-xs font-semibold">
                      {accounts[0]?.username?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-700 max-w-[120px] truncate">
                    {accounts[0]?.username?.split("@")[0]}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
                >
                  {t("Logout")}
                </button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-[#b5a666] transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#cabb78]"
            >
              <span className="sr-only">Open main menu</span>
              {!mobileMenuOpen ? (
                <svg
                  className="block h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              ) : (
                <svg
                  className="block h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`absolute top-14 left-0 right-0 md:hidden transition-all duration-200 ease-out ${
          mobileMenuOpen ? "max-h-72 opacity-100" : "max-h-0 opacity-0 pointer-events-none"
        } overflow-hidden bg-white border-b border-gray-200 shadow-lg`}
      >
        <div className="px-4 py-4 space-y-3 text-gray-700">
          <LanguageSelector />
          {accounts.length > 0 && (
            <>
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-gray-600 hover:text-[#b5a666]"
                >
                  {t("Admin")}
                </Link>
              )}
              <div className="flex items-center gap-3 py-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#cabb78] to-[#b5a666] flex items-center justify-center ring-2 ring-gray-100 flex-shrink-0">
                  <span className="text-white text-xs font-semibold">
                    {accounts[0]?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-700 truncate">
                  {accounts[0]?.username?.split("@")[0]}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="w-full text-left py-2 text-sm font-medium text-gray-500 hover:text-gray-800"
              >
                {t("Logout")}
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
